import React, { useState } from 'react';
import { Send, CheckCircle, Phone, Mail, Clock } from 'lucide-react';
import { submitConsultationForm } from '../data/mock';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    stage: '',
    serviceInterest: 'others',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitConsultationForm({ name: `${formData.firstName} ${formData.lastName}`, email: formData.email, company: formData.company, stage: formData.stage, serviceInterest: formData.serviceInterest, message: formData.message });
      setSubmitted(true);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', company: '', stage: '', serviceInterest: 'others', message: '' });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Page Header */}
      <section className="hero-gradient pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" data-testid="contact-title">
            Contact Us
          </h1>
          <p className="text-lg sm:text-xl text-white/75 max-w-2xl mx-auto leading-relaxed">
            With Avant-Garde Consulting, scale your teams today to conquer tomorrow.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 lg:py-28 bg-background px-4 sm:px-6 lg:px-8" data-testid="contact-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-3">
              <div className="card-depth rounded-2xl p-8 lg:p-10">
                {submitted ? (
                  <div className="flex flex-col items-center gap-6 text-center py-12" data-testid="contact-success">
                    <CheckCircle className="w-16 h-16 text-accent" />
                    <h3 className="text-2xl font-bold text-foreground">Thank You!</h3>
                    <p className="text-muted-foreground max-w-md">
                      We've received your inquiry and will reach out within 24 hours to schedule your consultation.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="rounded-lg border border-border px-6 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors"
                    >
                      Submit Another Request
                    </button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-foreground mb-8">Send us a message</h2>
                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">First Name *</label>
                          <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required
                            className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                            placeholder="John" data-testid="input-first-name" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">Last Name *</label>
                          <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required
                            className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                            placeholder="Smith" data-testid="input-last-name" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">Email *</label>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} required
                          className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                          placeholder="john@company.com" data-testid="input-email" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">Phone (Optional)</label>
                        <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                          className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                          placeholder="+1 (555) 000-0000" data-testid="input-phone" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">Company *</label>
                        <input type="text" name="company" value={formData.company} onChange={handleChange} required
                          className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                          placeholder="Your Company" data-testid="input-company" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">Company Stage</label>
                        <select name="stage" value={formData.stage} onChange={handleChange} data-testid="input-stage"
                          className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all">
                          <option value="">Select stage</option>
                          <option value="bootstrap">Bootstrapped</option>
                          <option value="pre-seed">Pre-Seed</option>
                          <option value="seed">Seed</option>
                          <option value="series-a">Series A</option>
                          <option value="series-b">Series B</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">Service Interest *</label>
                        <select name="serviceInterest" value={formData.serviceInterest} onChange={handleChange} required data-testid="input-service-interest"
                          className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all">
                          <option value="sr-leadership-hiring">Sr. Leadership Hiring</option>
                          <option value="ic-sales-role">IC Sales Role</option>
                          <option value="revenue-ops-crm">Revenue Operations and CRM Admins</option>
                          <option value="hr-it-recruitment">HR - IT Recruitment</option>
                          <option value="product-manager">Product Manager</option>
                          <option value="email-marketer">Email Marketer</option>
                          <option value="event-marketing-manager">Event Marketing Manager</option>
                          <option value="others">Others</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">Message</label>
                        <textarea name="message" value={formData.message} onChange={handleChange} rows={4}
                          className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-y"
                          placeholder="Tell us about your current situation and what you're looking to achieve..."
                          data-testid="input-message" />
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        data-testid="contact-submit-btn"
                        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-white font-semibold text-base px-6 py-3.5 hover:bg-primary/90 disabled:opacity-50 transition-all"
                      >
                        {loading ? 'Sending...' : 'Send Message'}
                        <Send className="w-4 h-4" />
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2" data-testid="contact-info">
              <h2 className="text-2xl font-bold text-foreground mb-8">Get in Touch</h2>
              <p className="text-muted-foreground leading-relaxed mb-10">
                We're here to help and answer any questions you might have. We look forward to hearing from you.
              </p>
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground mb-1">Phone</p>
                    <p className="text-muted-foreground text-sm">+91-9611344448</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground mb-1">Email</p>
                    <p className="text-muted-foreground text-sm">sameerpant@avant-gardeconsulting.co.in</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground mb-1">Business Hours</p>
                    <p className="text-muted-foreground text-sm">Monday - Friday: 9:00 AM - 6:00 PM</p>
                    <p className="text-muted-foreground text-sm">Saturday: 10:00 AM - 4:00 PM</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
